/**
 * FoliarSignOffPage — the manager's queue of completed foliar sessions
 * awaiting sign-off (F3 Task 11).
 *
 * Gated by the fertilizing:signoff menu, which is why this page exists as a
 * navigable destination rather than a button buried on the session screen.
 *
 * The error branch returns BEFORE the loading guard: a failed load must render
 * a notice, not sit on "loading" forever (the S1b/S2 brick-on-load lesson,
 * copied from SoilSignOffPage).
 *
 * Search/ageing/bulk decisions all live in ./foliarSignOff (and the shared
 * ./signOffAgeing) — this file is presentation, mirroring SoilSignOffPage's
 * shape with two deliberate departures:
 *   1. This table supplies a `mobileCardRow` — the queue is the screen a
 *      manager checks on a phone, and a control that exists only in a table
 *      column is unreachable there.
 *   2. The per-row action posts a ONE-ITEM batch to the bulk endpoint
 *      (Ruling 2), not the single-session route: that route is gated
 *      fertilizing:sessions while this tab is gated fertilizing:signoff, so a
 *      signoff-only manager would be 403'd on every row.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, FilterBar, Notice, StatusPill } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { FoliarWorkspaceContext } from './FoliarWorkspaceLayout';
import { nutritionService, FoliarAwaitingRow } from '../../services/nutritionService';
import { formatDate } from '../../dateFormat';
import {
  FoliarSignOffRow, buildSignOffRows, filterSignOffRows, toBulkItems, failedLabels,
} from './foliarSignOff';
import { WAITING_WARN_DAYS } from './signOffAgeing';
import './nutrition.css';

const FoliarSignOffPage: React.FC = () => {
  const { t } = useTranslation();
  // This page is a workspace TAB, so the layout hands it the resolved
  // programme. Null-safe because the suite renders it standalone, and because
  // useOutletContext returns null/undefined outside an <Outlet>.
  const workspace = useOutletContext<FoliarWorkspaceContext | null>();
  const programmeId = workspace?.programme?._id;
  const [items, setItems] = useState<FoliarAwaitingRow[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [search, setSearch] = useState('');
  const [overThresholdOnly, setOverThresholdOnly] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [partialFailed, setPartialFailed] = useState<string[]>([]);

  // Hooks must sit above every early return — a hook below a guard changes
  // hook order between renders and React throws.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    // Carry ?programme= or the layout falls back to the tenant's ACTIVE
    // programme and the crumb returns the user to a different board (#429).
    { label: t('nutrition.foliar.workspace.title'), to: programmeId ? `/nutrition/foliar?programme=${programmeId}` : '/nutrition/foliar' },
    { label: t('nutrition.foliar.workspace.tab.signOff') },
  ]);

  const load = useCallback(async () => {
    try {
      setItems(await nutritionService.listAwaitingFoliarSignoff());
      setError('');
    } catch (_err) {
      setError(t('nutrition.foliar.signOff.loadFailed'));
    }
    // t is intentionally omitted: react-i18next's `t` is not referentially
    // stable, and putting it in a dep array makes the effect re-fire on every
    // render (the S2 month-picker trap).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  // Ruling 2: the per-row action posts a ONE-ITEM batch to the bulk endpoint,
  // NOT nutritionSessionService.signOff — see the module comment above.
  const onSignOffRow = async (r: FoliarSignOffRow) => {
    setBusyId(r.sessionId);
    setPartialFailed([]);
    try {
      await nutritionService.signOffFoliarBulk([{ sessionId: r.sessionId }]);
      await load();                      // re-fetch: never splice local state
      // The row is gone after reload, but its id would otherwise linger in
      // `selected` forever — no checkbox remains to untick it, and the bulk
      // bar would stay visible with a dead "Sign off selected" button.
      setSelected((prev) => prev.filter((id) => id !== r.sessionId));
    } catch (_err) {
      setError(t('nutrition.foliar.signOff.signOffFailed'));
    } finally {
      setBusyId('');
    }
  };

  if (error && items === null) {
    return <div className="pat-page"><Notice tone="danger" testId="fso-error">{error}</Notice></div>;
  }
  if (items === null) return <div className="pat-page">{t('common.loading')}</div>;

  const rows: FoliarSignOffRow[] = buildSignOffRows(items, new Date());
  const shown = filterSignOffRows(rows, search, overThresholdOnly);
  const shownIds = shown.map((r) => r.sessionId);
  // Select-all must operate over the FILTERED rows, not the full set — a
  // manager who searched for one round must never bulk-sign the whole queue.
  const headerCheckedAll = shown.length > 0 && shown.every((r) => selected.includes(r.sessionId));
  // A selection deliberately survives a search/filter change (so selecting a
  // row then narrowing the search doesn't silently drop it from the bulk
  // run) — but that means the bulk bar can include sessions not currently on
  // screen. Surface the hidden count rather than pruning it: an undisclosed
  // selection is worse here, because sign-off is irreversible.
  const hiddenSelectedCount = selected.filter((id) => !shownIds.includes(id)).length;

  const toggleRow = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    setSelected(headerCheckedAll ? [] : shownIds.slice());
  };

  const clearSelection = () => setSelected([]);

  const onBulkApply = async () => {
    const bulkItems = toBulkItems(rows, selected);
    if (!bulkItems.length) return;
    setBulkBusy(true);
    setError('');
    setPartialFailed([]);
    let threw = false;
    try {
      const res = await nutritionService.signOffFoliarBulk(bulkItems);
      // `rows` is this render's snapshot — deliberately the PRE-reload rows,
      // since the failed sessions are named from the set that was submitted.
      setPartialFailed(res.failCount > 0 ? failedLabels(rows, res.results) : []);
      setSelected([]);
    } catch (_err) {
      // Selection deliberately survives a thrown error so the user can retry
      // the same set without reselecting.
      threw = true;
    } finally {
      // Re-fetch on BOTH paths, never splice local state. A request that
      // reached the server but lost its response leaves rows on screen that
      // are already signed off — the display would lie until the next visit.
      await load();
      // load() clears `error` when it succeeds, so the bulk failure is set
      // AFTER it — otherwise the reload would silently wipe the message.
      if (threw) setError(t('nutrition.foliar.signOff.bulkFailed'));
      setBulkBusy(false);
    }
  };

  // StatusPill takes only variant/children and does not spread props, so the
  // testid goes on a wrapper. Distinct ids for the table and card copies —
  // jsdom renders both and a shared id makes getByTestId ambiguous.
  const waitingPill = (r: FoliarSignOffRow, testId: string) => (
    <span data-testid={testId}>
      <StatusPill variant={r.tone}>
        {t('nutrition.foliar.signOff.waiting', { count: r.waitingDays })}
      </StatusPill>
    </span>
  );

  // ONE render function for the session-detail link, used by the column AND
  // the card — sign-off is irreversible, so the queue must offer a way to
  // inspect what is being approved rather than asking a manager to accept a
  // row of numbers on trust, and that must be true on a phone too. Distinct
  // ids for the table and card copies — jsdom renders both and a shared id
  // makes getByTestId ambiguous.
  const roundLink = (r: FoliarSignOffRow, testId: string, label: React.ReactNode) => (
    <Link to={`/nutrition/foliar/sessions/${r.sessionId}`} data-testid={testId}>
      {label}
    </Link>
  );

  // ONE render function for the action, used by the column AND the card.
  const signOffButton = (r: FoliarSignOffRow, testId: string) => (
    <button
      type="button" className="pat-btn pat-btn--primary"
      data-testid={testId}
      disabled={busyId === r.sessionId}
      onClick={() => onSignOffRow(r)}
    >
      {t('nutrition.foliar.signOff.action')}
    </button>
  );

  return (
    <div className="pat-page" data-testid="foliar-signoff-page">
      {error && <Notice tone="danger" testId="fso-error">{error}</Notice>}
      {partialFailed.length > 0 && (
        <Notice tone="warn" testId="fso-partial">
          {t('nutrition.foliar.signOff.partial', { count: partialFailed.length, names: partialFailed.join(', ') })}
        </Notice>
      )}

      {items.length === 0 ? (
        <div className="ntr-empty">
          <div className="ntr-empty__message">{t('nutrition.foliar.signOff.empty')}</div>
        </div>
      ) : (
        <div className="ntr-fso-body">
          {selected.length > 0 && (
            <div className="ntr-fso-bulk" data-testid="fso-bulk-bar">
              <span>{t('nutrition.foliar.signOff.selected', { count: selected.length })}</span>
              {hiddenSelectedCount > 0 && (
                <span data-testid="fso-bulk-hidden">
                  {t('nutrition.foliar.signOff.hiddenSelected', { count: hiddenSelectedCount })}
                </span>
              )}
              <button
                type="button" className="pat-btn pat-btn--primary"
                data-testid="fso-bulk-apply" disabled={bulkBusy}
                onClick={onBulkApply}
              >
                {t('nutrition.foliar.signOff.bulkApply')}
              </button>
              <button
                type="button" className="pat-btn pat-btn--subtle"
                data-testid="fso-bulk-clear" onClick={clearSelection}
              >
                {t('nutrition.foliar.signOff.clear')}
              </button>
            </div>
          )}

          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={t('nutrition.foliar.signOff.searchPlaceholder')}
            chips={[
              {
                id: 'overThreshold',
                label: t('nutrition.foliar.signOff.overThreshold', { count: WAITING_WARN_DAYS }),
                active: overThresholdOnly,
              },
            ]}
            onChipClick={(chipId) => {
              if (chipId === 'overThreshold') setOverThresholdOnly((prev) => !prev);
            }}
          />

          <DataTable<FoliarSignOffRow>
            rows={shown}
            rowKey={(r) => r.sessionId}
            emptyState={t('nutrition.foliar.signOff.noMatches')}
            columns={[
              {
                key: 'check',
                // label is ReactNode, so the select-all lives in the header where it belongs.
                label: (
                  <input
                    type="checkbox"
                    data-testid="fso-check-all"
                    checked={headerCheckedAll}
                    onChange={toggleAll}
                    aria-label={t('nutrition.foliar.signOff.selectAll')}
                  />
                ),
                render: (r) => (
                  <input
                    type="checkbox"
                    data-testid={`fso-check-${r.sessionId}`}
                    checked={selected.includes(r.sessionId)}
                    onChange={() => toggleRow(r.sessionId)}
                    aria-label={t('nutrition.foliar.signOff.selectRow', { label: r.label })}
                  />
                ),
              },
              {
                key: 'round',
                label: t('nutrition.foliar.signOff.col.round'),
                render: (r) => roundLink(r, `fso-open-${r.sessionId}`, r.roundName),
              },
              { key: 'orchard', label: t('nutrition.foliar.signOff.col.orchard'), render: (r) => r.orchardNo },
              {
                key: 'operator',
                label: t('nutrition.foliar.signOff.col.operator'),
                render: (r) => (r.operatorNames.length ? r.operatorNames.join(', ') : '—'),
              },
              {
                key: 'tanksApplied',
                label: t('nutrition.foliar.signOff.col.tanksApplied'),
                align: 'right',
                render: (r) => r.tanksApplied ?? '—',
              },
              {
                key: 'completedAt',
                label: t('nutrition.foliar.signOff.col.completed'),
                render: (r) => (r.completedAt ? formatDate(r.completedAt) : '—'),
              },
              {
                key: 'waiting',
                label: t('nutrition.foliar.signOff.col.waiting'),
                render: (r) => waitingPill(r, `fso-waiting-${r.sessionId}`),
              },
              {
                key: 'action',
                label: '',
                render: (r) => signOffButton(r, `fso-action-${r.sessionId}`),
              },
            ]}
            mobileCardRow={(r) => (
              <div className="ntr-fso-card">
                <input
                  type="checkbox"
                  data-testid={`fso-check-card-${r.sessionId}`}
                  checked={selected.includes(r.sessionId)}
                  onChange={() => toggleRow(r.sessionId)}
                  aria-label={t('nutrition.foliar.signOff.selectRow', { label: r.label })}
                />
                <div className="ntr-fso-card__body">
                  <strong>{roundLink(r, `fso-open-card-${r.sessionId}`, r.label)}</strong>
                  <div className="ntr-fso-card__meta">
                    {r.operatorNames.length ? r.operatorNames.join(', ') : '—'}
                    {' · '}
                    {t('nutrition.foliar.signOff.col.tanksApplied')}: {r.tanksApplied ?? '—'}
                  </div>
                  {waitingPill(r, `fso-waiting-card-${r.sessionId}`)}
                </div>
                {signOffButton(r, `fso-action-card-${r.sessionId}`)}
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default FoliarSignOffPage;
